import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDepartments, fetchHrDashboard } from "../../api/HrAPIs";
import { Card, Empty, HrFrame, HrGuard, inputClass, money } from "./hrUi";

export default function HrDashboardPage({ title = "لوحة الموارد البشرية" }) {
  return (
    <HrGuard>
      <Body title={title} />
    </HrGuard>
  );
}

function Body({ title }) {
  const now = new Date();
  const [departmentId, setDepartmentId] = useState("");
  const [from, setFrom] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`);
  const [to, setTo] = useState(now.toISOString().slice(0, 10));
  const departments = useQuery({ queryKey: ["hrDepartments"], queryFn: fetchDepartments });
  const dashboard = useQuery({
    queryKey: ["hrDashboard", from, to, departmentId],
    queryFn: () => fetchHrDashboard({ from, to, departmentId }),
  });
  const data = dashboard.data || {};
  const employees = data.employees;
  const attendance = data.attendance;
  const financial = data.financial;
  const performance = data.performance;
  const pending = data.pending;

  return (
    <HrFrame title={title} subtitle="الأرقام من قاعدة البيانات حسب صلاحياتك.">
      <div className="mb-4 grid gap-2 md:grid-cols-3">
        <input className={inputClass} type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        <input className={inputClass} type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        <select className={inputClass} value={departmentId} onChange={(event) => setDepartmentId(event.target.value)}>
          <option value="">كل الأقسام</option>
          {(departments.data || []).map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
        </select>
      </div>
      {dashboard.error && <p className="mb-3 text-sm text-rose-600">{dashboard.error.message}</p>}
      {employees && (
        <section className="mb-6">
          <h2 className="mb-2 font-bold">الموظفون</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Card title="الإجمالي" value={employees.total} />
            <Card title="النشطون" value={employees.active} />
            <Card title="مدراء الأقسام" value={(employees.managers || []).length} />
          </div>
        </section>
      )}
      {attendance && (
        <section className="mb-6">
          <h2 className="mb-2 font-bold">الحضور اليوم</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Card title="حاضر" value={attendance.presentToday} />
            <Card title="غائب" value={attendance.absentToday} />
            <Card title="متأخر" value={attendance.lateToday} />
            <Card title="تصحيحات" value={attendance.pendingCorrections} />
            <Card title="إجازات معلّقة" value={attendance.pendingLeaves} />
          </div>
        </section>
      )}
      {financial && (
        <section className="mb-6">
          <h2 className="mb-2 font-bold">المالي</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Card title="صافي المسير" value={money(financial.monthlyPayroll)} />
            <Card title="إضافي معتمد" value={financial.overtimePayments == null ? "مخفي" : money(financial.overtimePayments)} />
            <Card title="سلف قائمة" value={financial.outstandingAdvances == null ? "مخفي" : money(financial.outstandingAdvances)} />
            <Card title="رواتب لم تُصرف" value={money(financial.pendingSalaryPayments)} />
          </div>
        </section>
      )}
      {performance && (
        <section className="mb-6">
          <h2 className="mb-2 font-bold">الأداء</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Card title="متوسط التقييم" value={performance.averageRating ?? "—"} />
            <Card title="تقارير مكتملة" value={performance.reportsCompleted} />
            <Card title="تقارير ناقصة" value={performance.reportsMissing} />
          </div>
          {performance.tickets && (
            <p className="mt-3 text-sm text-slate-600">
              تذاكر: أُنشئت {performance.tickets.created} · مُسندة {performance.tickets.assigned} · تم التعامل {performance.tickets.handled} · حُلّت {performance.tickets.resolved} · أُغلقت {performance.tickets.closed} · أُعيد فتحها {performance.tickets.reopened} · معلقة {performance.tickets.pending}
            </p>
          )}
        </section>
      )}
      {pending && (
        <section>
          <h2 className="mb-2 font-bold">إجراءات معلّقة</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Card title="اعتماد إضافي" value={pending.overtime} />
            <Card title="اعتماد سلف" value={pending.advances} />
            <Card title="اعتماد رواتب" value={pending.payroll} />
            <Card title="إجازات" value={pending.leaves} />
            <Card title="تقارير ناقصة" value={pending.reports} />
          </div>
          {(performance?.missingReports || []).length === 0 ? <div className="mt-3"><Empty text="لا توجد تقارير ناقصة في نطاقك." /></div> : (
            <ul className="mt-3 text-sm">{performance.missingReports.map((item) => <li key={item.id}>{item.fullName}</li>)}</ul>
          )}
        </section>
      )}
    </HrFrame>
  );
}
